'use client'

import type { SerializedAllFeaturesEditorState } from '@/admin/lexical/features'
import type { Maybe } from '@/frontend-types'
import type { SubscribeFormData, SubscribeFormState } from '@/helpers/data'
import { subscribeToNewsletter } from '@/helpers/data'
import type { FC } from 'react'
import { useActionState, useEffect } from 'react'
import { Button } from '../atoms/Button'
import { FontAwesomeIcon } from '../atoms/FontAwesomeIcon'
import { FormInputCheckboxes } from '../atoms/FormInputCheckbox'
import FormInputText from '../atoms/FormInputText'
import InputError from '../atoms/InputError'
import { RenderLexicalContent } from '../RenderContentBlocks'

export type SubscribeFormFieldsProps = {
	state: SubscribeFormState
	pending: boolean
	termsAndConditions?: Maybe<SerializedAllFeaturesEditorState>
}

export const SubscribeFormFields: FC<SubscribeFormFieldsProps> = ({
	state: { values, errors },
	pending,
	termsAndConditions,
}) => {
	const formError = errors?.formErrors?.[0]

	const getFieldError = (field: keyof SubscribeFormData) => {
		return errors?.fieldErrors[field]?.[0]
	}

	return (
		<div>
			<div className="flex flex-col gap-y-xl mb-2xl">
				<FormInputText
					id="firstName"
					name="firstName"
					label="First Name"
					defaultValue={values.firstName}
					error={getFieldError('firstName')}
				/>
				<FormInputText
					id="lastName"
					name="lastName"
					label="Last Name"
					defaultValue={values.lastName}
					error={getFieldError('lastName')}
				/>
				<FormInputText
					id="postcode"
					name="postcode"
					label="Postcode"
					defaultValue={values.postcode}
					error={getFieldError('postcode')}
				/>
				<FormInputText
					id="email"
					name="email"
					label="Email"
					defaultValue={values.email}
					error={getFieldError('email')}
				/>
			</div>
			<div className="mb-2xl">
				<FormInputCheckboxes
					name="interestedIn"
					label="I’m interested in:"
					helpText="Select all that apply"
					options={[
						'All GIICA news and updates',
						'News and updates on SEQ venues only',
						'News and updates on regional venues only',
						'Industry updates, including procurement opportunities',
					]}
					defaultValue={values.interestedIn}
					error={getFieldError('interestedIn')}
				/>
			</div>
			{termsAndConditions && (
				<div className="mb-2xl **:text-body-xs!">
					<RenderLexicalContent data={termsAndConditions} layout="nested" />
				</div>
			)}
			<Button
				label="Subscribe"
				icon={['fas', 'chevron-right']}
				theme="white"
				intent="primary"
				type="submit"
				disabled={pending}
				className={pending ? 'cursor-not-allowed opacity-50' : ''}
			/>
			{formError && (
				<div className="mt-2">
					<InputError error={formError} />
				</div>
			)}
		</div>
	)
}

export type SubscribeFormProps = {
	termsAndConditions?: Maybe<SerializedAllFeaturesEditorState>
}

export const SubscribeForm: FC<SubscribeFormProps> = ({ termsAndConditions }) => {
	const [state, action, isPending] = useActionState(subscribeToNewsletter, {
		values: {
			firstName: '',
			lastName: '',
			postcode: '',
			email: '',
			interestedIn: [],
		},
		submitted: false,
	})

	// Smooth scroll up to subscribe form on submit
	useEffect(() => {
		if (state.submitted) {
			document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth' })
		}
	}, [state.submitted])

	if (state.submitted) {
		return (
			<div className="rounded-md bg-blue-tint-10 p-xl space-y-md">
				<div className="text-blue flex gap-md items-center">
					<FontAwesomeIcon icon={['fas', 'check-circle']} size="lg" />
					<p className="text-h4">Thank you</p>
				</div>
				<p>
					Thanks for submitting your details. We&apos;ll be in touch with news and updates on our
					projects soon.
				</p>
			</div>
		)
	}

	return (
		<form action={action}>
			<SubscribeFormFields
				state={state}
				pending={isPending}
				termsAndConditions={termsAndConditions}
			/>
		</form>
	)
}
